import { canonicalOrderChannelForSource } from '../contracts/operating-context.ts';
import type { OrderChannel, OrderSource, ServiceMode } from '../contracts/operating-context.ts';
import type { OperationalFact } from '../facts/operational-facts.ts';
import type { OrderLineSnapshot } from '../order/product-selection.ts';
import type { PriceResolution, PricingAdjustment, PricingLineEvidence } from '../order/order-finalization.ts';
import type { DayCloseReceipt } from '../business-day/business-day-coordinator.ts';
import type { AcceptedOrderReportingEvidence, ClosedDayReportingEvidence } from './reporting-types.ts';

const clone = <T>(value: T): T => structuredClone(value);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const isText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;
const isSafeInteger = (value: unknown): value is number => Number.isSafeInteger(value);
const isNonNegativeInteger = (value: unknown): value is number => isSafeInteger(value) && (value as number) >= 0;
const isPositiveInteger = (value: unknown): value is number => isSafeInteger(value) && (value as number) > 0;

const invalidAccepted = (): never => { throw new Error('REPORTING_ACCEPTED_FACT_INVALID'); };
const invalidReceipt = (): never => { throw new Error('REPORTING_DAY_CLOSE_RECEIPT_INVALID'); };

const asServiceMode = (value: unknown): ServiceMode | null => {
  if (value === undefined || value === null) return null;
  if (value === 'dine-in' || value === 'takeaway') return value;
  return invalidAccepted();
};

const validateLine = (value: unknown): OrderLineSnapshot => {
  if (!isRecord(value) || !isText(value.lineId) || !isText(value.productId) || !isPositiveInteger(value.quantity)) return invalidAccepted();
  if (!Array.isArray(value.selectorKeys) || value.selectorKeys.some((entry) => typeof entry !== 'string')) return invalidAccepted();
  if (!isRecord(value.productSnapshot) || !isText(value.productSnapshot.customerName) || !isText(value.productSnapshot.operationsName)) return invalidAccepted();
  if (!Array.isArray(value.productSnapshot.selectedOptions)) return invalidAccepted();
  for (const option of value.productSnapshot.selectedOptions) {
    if (!isRecord(option) || !isText(option.optionGroupId) || !isText(option.optionId) || !isText(option.name) || !isSafeInteger(option.priceDeltaMinor)) return invalidAccepted();
  }
  if (value.note !== null && value.note !== undefined && typeof value.note !== 'string') return invalidAccepted();
  return clone(value as unknown as OrderLineSnapshot);
};

const validatePricingLine = (value: unknown): PricingLineEvidence => {
  if (!isRecord(value) || !isText(value.lineId) || !isText(value.productId) || !isPositiveInteger(value.quantity)) return invalidAccepted();
  if (!isNonNegativeInteger(value.unitBaseMinor) || !isSafeInteger(value.unitModifierMinor) || !isNonNegativeInteger(value.lineSubtotalMinor)) return invalidAccepted();
  return clone(value as unknown as PricingLineEvidence);
};

const validateAdjustment = (value: unknown): PricingAdjustment => {
  if (!isRecord(value) || (value.kind !== 'combo' && value.kind !== 'discount' && value.kind !== 'surcharge')) return invalidAccepted();
  if (!isText(value.referenceId) || !isSafeInteger(value.amountMinor)) return invalidAccepted();
  if (value.consumedUnits !== undefined) {
    if (!Array.isArray(value.consumedUnits)) return invalidAccepted();
    for (const unit of value.consumedUnits) {
      if (!isRecord(unit) || !isText(unit.lineId) || !isNonNegativeInteger(unit.unitIndex)) return invalidAccepted();
    }
  }
  return clone(value as unknown as PricingAdjustment);
};

const validatePriceResolution = (value: unknown): PriceResolution => {
  if (!isRecord(value) || !isNonNegativeInteger(value.authoritativeTotalMinor) || !isSafeInteger(value.differenceMinor) || !isNonNegativeInteger(value.finalTotalMinor)) return invalidAccepted();
  if (value.action !== 'matched' && value.action !== 'customer_confirmed_authority' && value.action !== 'store_accepted_displayed') return invalidAccepted();
  if (value.customerDisplayedTotalMinor !== undefined && !isNonNegativeInteger(value.customerDisplayedTotalMinor)) return invalidAccepted();
  if (value.operatorId !== undefined && !isText(value.operatorId)) return invalidAccepted();
  if (value.resolvedAt !== undefined && !Number.isFinite(Date.parse(String(value.resolvedAt)))) return invalidAccepted();
  if (value.reason !== undefined && typeof value.reason !== 'string') return invalidAccepted();
  return clone(value as unknown as PriceResolution);
};

const validateLineEvidenceConsistency = (
  lines: readonly OrderLineSnapshot[],
  pricingLines: readonly PricingLineEvidence[],
): void => {
  if (lines.length !== pricingLines.length) return invalidAccepted();
  const byId = new Map(lines.map((line) => [line.lineId, line]));
  if (byId.size !== lines.length) return invalidAccepted();
  for (const price of pricingLines) {
    const line = byId.get(price.lineId);
    if (!line || line.productId !== price.productId || line.quantity !== price.quantity) return invalidAccepted();
  }
};

export function readAcceptedOrderEvidence(fact: OperationalFact): AcceptedOrderReportingEvidence {
  if (fact.factType !== 'order.accepted' || fact.aggregateType !== 'order' || !isRecord(fact.payload)) return invalidAccepted();
  if (!isText(fact.aggregateId) || !isText(fact.eventId) || !isText(fact.businessDate) || !Number.isFinite(Date.parse(fact.occurredAt))) return invalidAccepted();
  if (!isText(fact.source?.revision)) return invalidAccepted();

  const payload = fact.payload;
  if (!isText(payload.source) || !isText(payload.channel) || !isText(payload.currency) || !isNonNegativeInteger(payload.totalMinor)) return invalidAccepted();
  const source = payload.source as OrderSource;
  const canonicalChannel = canonicalOrderChannelForSource(source);
  if (!canonicalChannel || canonicalChannel !== payload.channel) return invalidAccepted();
  const serviceMode = asServiceMode(payload.serviceMode);

  const evidenceKeys = ['lines', 'pricingLines', 'pricingAdjustments', 'priceResolution'] as const;
  const presentCount = evidenceKeys.filter((key) => payload[key] !== undefined).length;
  if (presentCount !== 0 && presentCount !== evidenceKeys.length) return invalidAccepted();

  if (presentCount === 0) {
    return {
      orderId: fact.aggregateId,
      businessDate: fact.businessDate,
      occurredAt: fact.occurredAt,
      source,
      channel: canonicalChannel as OrderChannel,
      serviceMode,
      currency: payload.currency,
      totalMinor: payload.totalMinor,
      lines: [],
      pricingLines: [],
      pricingAdjustments: [],
      priceResolution: null,
      productSourceSafety: 'source_not_established',
      sourceEventId: fact.eventId,
      sourceRevision: fact.source.revision,
    };
  }

  if (!Array.isArray(payload.lines) || !Array.isArray(payload.pricingLines) || !Array.isArray(payload.pricingAdjustments)) return invalidAccepted();
  const lines = payload.lines.map(validateLine);
  const pricingLines = payload.pricingLines.map(validatePricingLine);
  const pricingAdjustments = payload.pricingAdjustments.map(validateAdjustment);
  const priceResolution = validatePriceResolution(payload.priceResolution);
  validateLineEvidenceConsistency(lines, pricingLines);
  if (priceResolution.finalTotalMinor !== payload.totalMinor) return invalidAccepted();

  return {
    orderId: fact.aggregateId,
    businessDate: fact.businessDate,
    occurredAt: fact.occurredAt,
    source,
    channel: canonicalChannel as OrderChannel,
    serviceMode,
    currency: payload.currency,
    totalMinor: payload.totalMinor,
    lines,
    pricingLines,
    pricingAdjustments,
    priceResolution,
    productSourceSafety: 'established',
    sourceEventId: fact.eventId,
    sourceRevision: fact.source.revision,
  };
}

const validateReceiptFinancials = (payload: Record<string, unknown>): void => {
  const nonNegative = [
    'totalOrderCount', 'grossOrderMinor', 'receivedMinor', 'refundMinor', 'correctionInMinor', 'correctionOutMinor',
    'openingCashMinor', 'cashInMinor', 'cashOutMinor', 'countedCashMinor', 'closingWithdrawalMinor', 'retainedCashMinor',
  ];
  for (const key of nonNegative) if (!isNonNegativeInteger(payload[key])) return invalidReceipt();
  for (const key of ['netReceivedMinor', 'cashPaymentNetMinor', 'expectedCashMinor', 'varianceMinor']) {
    if (!isSafeInteger(payload[key])) return invalidReceipt();
  }
  if (payload.variancePercent !== null && typeof payload.variancePercent !== 'number') return invalidReceipt();
  for (const key of ['orderBreakdown', 'paymentBreakdown', 'adjustments', 'hardBlockers', 'businessAlerts', 'overrides']) {
    if (!Array.isArray(payload[key])) return invalidReceipt();
  }
};

export function readClosedDayReceipt(fact: OperationalFact): ClosedDayReportingEvidence {
  if (fact.factType !== 'day_close.receipt' || fact.aggregateType !== 'business_day' || !isRecord(fact.payload)) return invalidReceipt();
  const payload = fact.payload;
  if (!isText(fact.eventId) || !isText(fact.source?.revision) || !isText(payload.closeId) || !isText(payload.storeId) || !isText(payload.businessDate)) return invalidReceipt();
  if (payload.businessDate !== fact.businessDate) return invalidReceipt();
  validateReceiptFinancials(payload);
  return {
    mode: 'FINAL',
    receipt: clone(payload as unknown as DayCloseReceipt),
    sourceEventId: fact.eventId,
    sourceRevision: fact.source.revision,
  };
}
